/** @jsx isolateComponent */

import { css } from '@emotion/react';
import {
  Animation,
  Colors,
  useClickOutside,
  ZapierLogo,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useState } from 'react';
import {
  skipLinkHref as skipLinkHrefDefault,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '../../constants';
import { useUniversalNavContext } from '../../hooks';
import SkipLink from '../SkipLink';
import UniversalTopbarLink from './UniversalTopbarLink';

import AccountActions from './AccountActions';
import SearchForm from './SearchForm';
import SearchToggler from './SearchToggler';
import { emitUserInteractionEvent } from '../../utils/avroEvents';

import type { Props as SearchFormProps } from './SearchForm';
import type { NavItem } from '../../constants/links';

export type Props = {
  /*
   * Indicates whether search fills the entire topbar or not.
   */
  isSearchFullWidth: boolean;
  /**
   * An array of links to be rendered inside the main navigation.
   */
  links?: Array<NavItem>;
  /**
   * Zapier logo label for link
   */
  logoAriaLabel?: string;
  /**
   * Zapier logo link
   */
  logoHref?: string;
  /**
   * Optional handler for when anywhere on the top nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when somewhere outside of the nav is clicked.
   */
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  /**
   * Zapier logo link callback
   */
  onLogoClick?: () => void;
  /**
   * Renders an account menu if provided. This overrides the default account actions menu (Log in, Sign up)
   */
  renderAccountMenu?: (props: Props) => ReactNode;
  /**
   * Renders notifications component if provided
   */
  renderNotifications?: (props: Props) => ReactNode;
  /**
   * Renders search component, or prevents it from rendering
   */
  renderSearchForm?: (props: SearchFormProps) => ReactNode;
  /**
   * Render the sidebar component if provided
   */
  renderSidebar?: (props: Props) => ReactNode;
  /**
   * Currently selected link inside links
   */
  selectedHref?: string;
  /**
   * Indicates whether the logo should render.
   */
  shouldRenderLogo?: boolean;
  /**
   * Size of the topbar which adjusts its height, padding, etc.
   *
   * Related to `props.height`, with each serving different purposes.
   */
  size?: 'small' | 'medium';
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref?: string;
};

type LinksProps = Props & {
  isSearching: boolean;
};

const Styles = labelStyles('UniversalTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `${Colors.neutral100}`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.neutral300}`,
    },
  ],
  header: ({ hasSidebar }: { hasSidebar: boolean }) => [
    css({
      width: '100%',
      maxWidth: '1200px',
      height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
      padding: '0 22px 0 10px',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      alignItems: 'stretch',
      gap: '5px',
      backgroundColor: Colors.neutral100,

      '@media (min-width: 450px)': {
        height: UNIVERSAL_TOPBAR_HEIGHT,
        padding: '0 20px',
        gap: '0',
      },
    }),
    hasSidebar &&
      withModifier(
        'has-sidebar',
        css({
          maxWidth: 'none',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: '10px',
        })
      ),
  ],
  group: css({
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifySelf: 'flex-end',
    gap: '5px',

    '@media (min-width: 450px)': {
      gap: '17px',
    },
  }),
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],
  searchForm: (props: Props, { isSearching }: { isSearching: boolean }) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        backgroundColor: `${Colors.neutral100}`,
        padding: '0 10px',
      }),
  ],
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      marginRight: -7,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  navLinksList: (props: LinksProps) => [
    {
      display: 'grid',
      ...(props.links && {
        // Accounts for static product tab whose hard-coded data won't be in props.links
        gridTemplateColumns: `repeat(${props.links.length + 1}, max-content)`,
      }),
    },
    props.isSearching &&
      withModifier('searching', {
        opacity: 0,
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: LinksProps) => {
  const [activeMenu, setActiveMenu] = useState<string>('');
  const [activeElement, setActiveElement] = useState<Element | null>(null);

  // Clicking outside navigation area closes menu
  const [setClickOutsideNav] = useClickOutside({
    onClickOutside: () => setActiveMenu(''),
  });

  const toggleMegaMenu = (menuId: string) => {
    if (activeMenu === menuId) {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    } else {
      if (activeElement instanceof HTMLElement)
        setActiveElement(document.activeElement);
      setActiveMenu(menuId);
    }
  };

  const closeMenu = () => setActiveMenu('');

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    }
  };

  // escape key closes menu
  useEffect(() => {
    document.addEventListener('keydown', handleEscape, { capture: false });

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: false });
    };
  });

  const links = props.links?.map((link) => (
    <UniversalTopbarLink
      ariaControls={link.text}
      isMenuOpen={activeMenu === link.text}
      key={link.text}
      navigationItem={link}
      onCloseMenu={closeMenu}
      position="navigation"
      toggleMenu={() => toggleMegaMenu(link.text)}
    >
      {link.text}
    </UniversalTopbarLink>
  ));
  if (!links) {
    return null;
  }
  return (
    <nav aria-label="Site Links" id="site-nav" ref={setClickOutsideNav}>
      <ul css={Styles.navLinksList(props)}>{links}</ul>
    </nav>
  );
};

/**
 * @private
 * Renders universal dynamic navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalTopbar = ({
  isSearchFullWidth,
  links = undefined,
  logoAriaLabel = 'Go to the Zapier Homepage',
  logoHref = '/',
  onLogoClick = () => {},
  selectedHref = '',
  shouldRenderLogo = true,
  size = 'medium',
  skipLinkHref = skipLinkHrefDefault,
  renderAccountMenu = (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications = () => null,
  renderSearchForm = (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  ...otherProps
}: Props) => {
  const {
    currentUrl,
    getFullyQualifiedURL,
    shouldRenderCallToAction,
    topNavData,
  } = useUniversalNavContext();

  const props = {
    isSearchFullWidth,
    links,
    logoAriaLabel,
    logoHref,
    onLogoClick,
    renderAccountMenu,
    renderNotifications,
    renderSearchForm,
    selectedHref,
    shouldRenderLogo,
    size,
    skipLinkHref,
    ...otherProps,
  } satisfies Props;

  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    width: 105,
    height: 28,
  };

  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });
  const callToActionUrl =
    getFullyQualifiedURL(
      `${topNavData.callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
    ) || '';

  return (
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header({ hasSidebar: !!sidebar })}
        data-testid="universalTopbar"
        onClick={props.onClick}
      >
        <div css={Styles.group}>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink}>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent}>
          {links && links.length > 0 && (
            <Links {...props} isSearching={isSearching} links={links} />
          )}
          {
            <div
              aria-label="Search apps"
              css={Styles.searchForm(props, { isSearching })}
              onBlur={() => {
                setIsSearching(false);
              }}
              onFocus={() => {
                setIsSearching(true);
                emitUserInteractionEvent({
                  interaction_name: searchInputId,
                  event_action: 'in_view',
                  option_selected: 'Universal Nav Search opened',
                });
              }}
              role="form"
            >
              {searchForm}
            </div>
          }
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group}
          role="form"
        >
          {!!searchForm && (
            <div
              css={Styles.searchToggler({ isSearching })}
              data-testid="searchToggler"
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                label="Search apps…"
              />
            </div>
          )}

          {topNavData.callToAction &&
            props.size !== 'small' &&
            shouldRenderCallToAction && (
              <UniversalTopbarLink
                navigationItem={{
                  variant: 'link',
                  ...topNavData.callToAction,
                  href: callToActionUrl,
                }}
                position="actions-menu"
              >
                {topNavData.callToAction.text}
              </UniversalTopbarLink>
            )}

          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

export default UniversalTopbar;
